<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';
import {
    message,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    Button,
    Table,
    Card,
    DatePicker,
    Segmented,
    Tag
} from 'ant-design-vue';
import {
    getConsultants,
    getConsultationLevels,
    getConsultantsCompliance,
    updateConsultant
} from '@/api/consultation/consultationApi';
import { usePermission } from '@/composables/usePermission';

const route = useRoute();
const router = useRouter();
const { canView, canUpdate } = usePermission('Consultants');
const { canView: canViewConsultations } = usePermission('Consultations');

const loading = ref(false);
const list = ref([]);
const complianceItems = ref([]);
const complianceSummary = ref({ total: 0, compliant: 0, non_compliant: 0 });
const editOpen = ref(false);
const editLoading = ref(false);
const current = ref(null);
const consultationLevels = ref([]);

const scheduleMonth = ref(dayjs().format('YYYY-MM'));
const filterLevel = ref('');
const complianceFilter = ref('all');

const levelOptions = computed(() =>
    consultationLevels.value
        .filter((l) => l.active !== false)
        .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
        .map((l) => ({
            label: l.name || l.code,
            value: l.code
        }))
);

const levelFilterOptions = computed(() => [
    { label: '全部等級', value: '' },
    ...levelOptions.value
]);

const levelNameByCode = computed(() =>
    Object.fromEntries(
        consultationLevels.value.map((l) => [l.code, l.name || l.code])
    )
);

const levelLabel = (code) => levelNameByCode.value[code] || code || '—';

const complianceByCmid = computed(() =>
    Object.fromEntries(complianceItems.value.map((i) => [i.core_member_id, i]))
);

const enrichedList = computed(() =>
    list.value.map((c) => ({
        ...c,
        compliance: complianceByCmid.value[c.core_member_id] ?? null
    }))
);

const filteredList = computed(() => {
    let rows = enrichedList.value;
    if (filterLevel.value) {
        rows = rows.filter((c) => c.level === filterLevel.value);
    }
    if (complianceFilter.value === 'compliant') {
        rows = rows.filter((c) => c.compliance?.is_compliant);
    } else if (complianceFilter.value === 'non_compliant') {
        rows = rows.filter(
            (c) =>
                c.compliance &&
                c.compliance.min_monthly_open_slots > 0 &&
                !c.compliance.is_compliant
        );
    }
    return rows;
});

const boundManagerParts = (record) => {
    const m = record?.bound_manager;
    if (!m) return { primary: '—', secondary: '' };
    const email = m.email || '';
    const name = m.name || '';
    if (email && name) return { primary: email, secondary: name };
    if (email) return { primary: email, secondary: '' };
    if (name) return { primary: name, secondary: '' };
    return { primary: `ID ${m.id}`, secondary: '' };
};

const scheduleProgressLabel = (record) => {
    const c = record.compliance;
    if (!c || !record.active) return '—';
    if (c.min_monthly_open_slots <= 0) return '不強制';
    return `${c.open_count} / ${c.min_monthly_open_slots}`;
};

const columns = [
    { title: '諮詢師', dataIndex: 'display_name', key: 'dn', width: 120, ellipsis: true },
    { title: '管理者帳號', key: 'mgr', width: 240, ellipsis: true },
    { title: '等級', dataIndex: 'level', key: 'lv', width: 100 },
    {
        title: '本月開放',
        key: 'schedule',
        width: 120,
        align: 'center'
    },
    { title: '達標', key: 'compliant', width: 96, align: 'center' },
    { title: '啟用', key: 'ac', width: 88, align: 'center' },
    {
        title: '本月接單',
        dataIndex: 'bookings_this_month',
        key: 'bm',
        width: 96,
        align: 'center'
    },
    { title: '操作', key: 'actions', width: 268, fixed: 'right' }
];

const goSchedule = (record) => {
    router.push({
        name: 'ConsultantSchedule',
        params: { coreMemberId: record.core_member_id }
    });
};

const goBookings = (record) => {
    router.push({
        name: 'Consultations',
        query: {
            consultant_core_member_id: record.core_member_id,
            from: dayjs(scheduleMonth.value).startOf('month').format('YYYY-MM-DD'),
            to: dayjs(scheduleMonth.value).endOf('month').format('YYYY-MM-DD')
        }
    });
};

const loadLevels = async () => {
    const res = await getConsultationLevels();
    if (res.status !== 1) throw new Error(res.message || '載入等級失敗');
    consultationLevels.value = res.data || [];
};

const loadCompliance = async () => {
    const res = await getConsultantsCompliance({ month: scheduleMonth.value });
    if (res.status !== 1) throw new Error(res.message || '載入排班達標失敗');
    complianceItems.value = res.data?.items ?? [];
    complianceSummary.value = res.data?.summary ?? {
        total: 0,
        compliant: 0,
        non_compliant: 0
    };
};

const load = async () => {
    loading.value = true;
    try {
        const [res] = await Promise.all([getConsultants(), loadLevels(), loadCompliance()]);
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        list.value = res.data || [];
    } catch (e) {
        message.error(e.message || '載入失敗');
    } finally {
        loading.value = false;
    }
};

const openEdit = (row) => {
    current.value = { ...row };
    editOpen.value = true;
};

const save = async () => {
    if (!current.value) return;
    editLoading.value = true;
    try {
        const res = await updateConsultant(current.value.id, {
            display_name: current.value.display_name,
            bio: current.value.bio,
            level: current.value.level,
            active: current.value.active
        });
        if (res.status !== 1) throw new Error(res.message || '儲存失敗');
        message.success('已儲存');
        editOpen.value = false;
        await load();
    } catch (e) {
        message.error(e.message || '儲存失敗');
    } finally {
        editLoading.value = false;
    }
};

const applyRouteLevelFilter = () => {
    const code = typeof route.query.level === 'string' ? route.query.level : '';
    filterLevel.value = code;
};

watch(scheduleMonth, () => {
    loadCompliance().catch((e) => message.error(e.message || '載入排班達標失敗'));
});

watch(
    () => route.query.level,
    () => applyRouteLevelFilter()
);

onMounted(() => {
    applyRouteLevelFilter();
    load();
});
</script>

<template>
    <div class="consultants-page pb-10">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>

        <Card
            v-else
            :bordered="false"
            class="admin-panel overflow-hidden rounded-2xl border border-[#ebe4d6] bg-white shadow-md"
        >
            <div class="consultants-toolbar">
                <div class="consultants-toolbar__main">
                    <label class="consultants-toolbar__field">
                        <span class="consultants-toolbar__label">月份</span>
                        <DatePicker
                            v-model:value="scheduleMonth"
                            picker="month"
                            value-format="YYYY-MM"
                            :allow-clear="false"
                            class="consultants-toolbar__month"
                        />
                    </label>
                    <label class="consultants-toolbar__field">
                        <span class="consultants-toolbar__label">等級</span>
                        <Select
                            v-model:value="filterLevel"
                            :options="levelFilterOptions"
                            placeholder="全部"
                            class="consultants-toolbar__level"
                            allow-clear
                        />
                    </label>
                    <div class="consultants-toolbar__stats" aria-label="本月達標摘要">
                        <span class="consultants-stat consultants-stat--muted">
                            共 {{ complianceSummary.total }}
                        </span>
                        <span class="consultants-stat consultants-stat--ok">
                            達標 {{ complianceSummary.compliant }}
                        </span>
                        <span class="consultants-stat consultants-stat--bad">
                            未達標 {{ complianceSummary.non_compliant }}
                        </span>
                    </div>
                </div>
                <div class="consultants-toolbar__status">
                    <Segmented
                        v-model:value="complianceFilter"
                        size="middle"
                        :options="[
                            { label: '全部', value: 'all' },
                            {
                                label: `達標 ${complianceSummary.compliant}`,
                                value: 'compliant'
                            },
                            {
                                label: `未達標 ${complianceSummary.non_compliant}`,
                                value: 'non_compliant'
                            }
                        ]"
                    />
                </div>
            </div>

            <Table
                class="admin-table-surface consultants-table"
                :columns="columns"
                :data-source="filteredList"
                :loading="loading"
                row-key="id"
                :pagination="{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    showTotal: (t) => `共 ${t} 位`
                }"
                size="middle"
                :scroll="{ x: 1180 }"
                :locale="{ emptyText: '尚無符合條件的諮詢師' }"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'dn'">
                        <div class="consultants-name" :title="record.display_name">
                            {{ record.display_name || '—' }}
                        </div>
                    </template>
                    <template v-else-if="column.key === 'lv'">
                        <Tag v-if="record.level" class="consultants-level-tag m-0">
                            {{ levelLabel(record.level) }}
                        </Tag>
                        <span v-else class="text-slate-400">—</span>
                    </template>
                    <template v-else-if="column.key === 'mgr'">
                        <div
                            class="consultants-mgr"
                            :title="
                                [boundManagerParts(record).primary, boundManagerParts(record).secondary]
                                    .filter(Boolean)
                                    .join(' · ')
                            "
                        >
                            <div class="consultants-mgr__primary">
                                {{ boundManagerParts(record).primary }}
                            </div>
                            <div
                                v-if="boundManagerParts(record).secondary"
                                class="consultants-mgr__secondary"
                            >
                                {{ boundManagerParts(record).secondary }}
                            </div>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'schedule'">
                        <span
                            v-if="
                                record.compliance &&
                                record.active &&
                                record.compliance.min_monthly_open_slots <= 0
                            "
                            class="consultants-schedule consultants-schedule--optional"
                        >
                            不強制
                        </span>
                        <span
                            v-else-if="record.compliance && record.active"
                            class="consultants-schedule tabular-nums"
                            :class="
                                !record.compliance.is_compliant
                                    ? 'consultants-schedule--warn'
                                    : 'consultants-schedule--ok'
                            "
                        >
                            {{ scheduleProgressLabel(record) }}
                        </span>
                        <span v-else class="text-slate-400">—</span>
                    </template>
                    <template v-else-if="column.key === 'compliant'">
                        <template v-if="record.compliance && record.active">
                            <Tag
                                v-if="record.compliance.min_monthly_open_slots <= 0"
                                color="default"
                                class="m-0"
                            >
                                —
                            </Tag>
                            <Tag
                                v-else-if="record.compliance.is_compliant"
                                color="success"
                                class="m-0"
                            >
                                達標
                            </Tag>
                            <Tag v-else color="error" class="m-0">
                                差 {{ record.compliance.gap }}
                            </Tag>
                        </template>
                        <span v-else class="text-slate-400">—</span>
                    </template>
                    <template v-else-if="column.key === 'ac'">
                        <Tag :color="record.active ? 'success' : 'default'" class="m-0">
                            {{ record.active ? '啟用' : '停用' }}
                        </Tag>
                    </template>
                    <template v-else-if="column.key === 'bm'">
                        <span class="consultants-bookings tabular-nums">
                            {{ record.bookings_this_month ?? 0 }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'actions'">
                        <div class="admin-table-actions">
                            <Button
                                type="primary"
                                ghost
                                size="small"
                                class="admin-table-actions__btn"
                                @click="goSchedule(record)"
                            >
                                查看排班
                            </Button>
                            <Button
                                v-if="canViewConsultations"
                                size="small"
                                class="admin-table-actions__btn"
                                @click="goBookings(record)"
                            >
                                預約紀錄
                            </Button>
                            <Button
                                size="small"
                                class="admin-table-actions__btn"
                                :disabled="!canUpdate"
                                @click="openEdit(record)"
                            >
                                編輯
                            </Button>
                        </div>
                    </template>
                </template>
            </Table>
            <p
                v-if="!loading && list.length === 0"
                class="mt-0 px-5 pb-5 text-sm leading-relaxed text-slate-500"
            >
                諮詢師須由系統開通同步建立後才會出現於此列表。
            </p>
        </Card>

        <Modal
            v-model:open="editOpen"
            title="編輯諮詢師"
            wrap-class-name="edit-modal"
            :confirm-loading="editLoading"
            ok-text="儲存"
            cancel-text="取消"
            @ok="save"
        >
            <Form v-if="current" layout="vertical">
                <Form.Item label="顯示名稱">
                    <Input v-model:value="current.display_name" />
                </Form.Item>
                <Form.Item label="簡介" extra="目前僅供後台儲存，前台預約流程尚未顯示">
                    <Input.TextArea v-model:value="current.bio" :rows="3" />
                </Form.Item>
                <Form.Item label="等級">
                    <Select
                        v-model:value="current.level"
                        :options="levelOptions"
                        placeholder="請選擇等級"
                        allow-clear
                    />
                </Form.Item>
                <Form.Item label="啟用">
                    <Switch v-model:checked="current.active" />
                </Form.Item>
            </Form>
        </Modal>
    </div>
</template>

<style scoped>
.consultants-page {
    max-width: 1360px;
}

.consultants-toolbar {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin: -4px -6px 4px;
    padding: 4px 6px 16px;
    border-bottom: 1px solid #f0ebe3;
}

.consultants-toolbar__main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px 18px;
}

.consultants-toolbar__status {
    display: flex;
}

.consultants-toolbar__status :deep(.ant-segmented) {
    background: #f6f3ed;
    border-radius: 10px;
    padding: 3px;
}

.consultants-toolbar__field {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
}

.consultants-toolbar__label {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;
}

.consultants-toolbar__month {
    width: 140px;
}

.consultants-toolbar__level {
    min-width: 140px;
}

.consultants-toolbar__stats {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.consultants-stat {
    display: inline-flex;
    align-items: center;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.01em;
}

.consultants-stat--muted {
    color: #64748b;
    background: #f1f5f9;
}

.consultants-stat--ok {
    color: #1f6b4a;
    background: #e8f6ef;
}

.consultants-stat--bad {
    color: #b42318;
    background: #fef3f2;
}

.consultants-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.35;
}

.consultants-mgr {
    min-width: 0;
}

.consultants-mgr__primary {
    font-size: 13px;
    line-height: 1.35;
    color: #475569;
    word-break: break-all;
}

.consultants-mgr__secondary {
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.3;
    color: #94a3b8;
}

.consultants-level-tag {
    border-color: #e8dfcd !important;
    background: #faf6ee !important;
    color: #6b5428 !important;
    font-weight: 600;
}

.consultants-schedule {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 4.5rem;
    height: 26px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
}

.consultants-schedule--ok {
    color: #1f6b4a;
    background: #eef8f2;
}

.consultants-schedule--warn {
    color: #b42318;
    background: #fef3f2;
}

.consultants-schedule--optional {
    color: #64748b;
    background: #f8fafc;
    font-weight: 500;
}

.consultants-bookings {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
}

.consultants-table :deep(.ant-table-tbody > tr > td) {
    vertical-align: middle;
    padding-top: 14px;
    padding-bottom: 14px;
}

.consultants-table :deep(.ant-pagination) {
    margin: 16px 4px 4px;
}

@media (max-width: 900px) {
    .consultants-toolbar__stats {
        margin-left: 0;
        width: 100%;
    }
}
</style>
